import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Tabs,
  Tab,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  IconButton,
  Box,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
Sms as SmsIcon,
  Person as UserIcon,
  Assessment as AuditIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface NavItem {
  label: string;
  icon: React.ReactElement;
  path: string;
  roles: Array<'ADMIN' | 'USER'>;
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/',
    roles: ['ADMIN', 'USER'],
  },
{
    label: 'SMS Logs',
    icon: <SmsIcon />,
    path: '/sms-logs',
    roles: ['ADMIN'],
  },
  {
    label: 'Users',
    icon: <UserIcon />,
    path: '/users',
    roles: ['ADMIN'],
  },
  {
    label: 'Audit Trail',
    icon: <AuditIcon />,
    path: '/audit',
    roles: ['ADMIN'],
  },
  {
    label: 'Settings',
    icon: <SettingsIcon />,
    path: '/settings',
    roles: ['ADMIN'],
  },
];

interface NavigationProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ mobileOpen = false, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  // Filter nav items based on user role
  const filteredNavItems = navItems.filter(
    (item) => user && item.roles.includes(user.role)
  );

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const currentTab = filteredNavItems.findIndex((item) => item.path === location.pathname);

  // Desktop: Horizontal Tabs
  if (!isMobile) {
    return (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={currentTab >= 0 ? currentTab : 0}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            '& .MuiTab-root': {
              minHeight: 64,
              minWidth: 120,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
            },
          }}
        >
          {filteredNavItems.map((item) => (
            <Tab
              key={item.path}
              label={item.label}
              icon={item.icon}
              iconPosition="start"
              onClick={() => handleNavigation(item.path)}
              sx={{
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>
    );
  }

  // Mobile: Drawer
  return (
    <Drawer
      anchor="left"
      open={mobileOpen}
      onClose={onMobileClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Box sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          SMS Reminder System
        </Box>
        <IconButton onClick={onMobileClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {filteredNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                minHeight: 56,
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  '&:hover': {
                    bgcolor: 'action.selected',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path ? 'primary.main' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  color: location.pathname === item.path ? 'primary.main' : 'inherit',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};

export const MobileMenuButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!isMobile) return null;

  return (
    <IconButton
      edge="start"
      color="inherit"
      aria-label="menu"
      onClick={onClick}
      sx={{ mr: 2 }}
    >
      <MenuIcon />
    </IconButton>
  );
};
